package com.presentocracy.shared.validation

import com.presentocracy.shared.model.Birthday

object UserInputRules {
    const val MIN_PASSWORD_LENGTH = 8
    const val MAX_PASSWORD_LENGTH = 256
    private val usernamePattern = Regex("^[a-z0-9_]{3,24}$")
    private val emailPattern = Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")
    private val currencyPattern = Regex("^[A-Z]{3}$")

    val reservedUsernames: Set<String> = setOf(
        "w", "g", "api", "auth", "settings", "admin", "login", "register", "invite",
        "me", "users", "friends", "birthdays", "notifications",
    )

    fun normalizeEmail(value: String): String = value.trim().lowercase()

    fun validateEmail(value: String): String? {
        val normalized = normalizeEmail(value)
        return when {
            normalized.length > 320 -> "Email is too long"
            !emailPattern.matches(normalized) -> "Enter a valid email address"
            else -> null
        }
    }

    fun normalizeUsername(value: String): String = value.trim().lowercase()

    fun validateUsername(value: String): String? {
        val normalized = normalizeUsername(value)
        return when {
            !usernamePattern.matches(normalized) -> "Username must be 3-24 characters using letters, numbers, or underscores"
            normalized in reservedUsernames -> "That username is reserved"
            else -> null
        }
    }

    fun validatePassword(value: String): String? = when {
        value.length < MIN_PASSWORD_LENGTH -> "Password must be at least $MIN_PASSWORD_LENGTH characters"
        value.length > MAX_PASSWORD_LENGTH -> "Password is too long"
        !value.any(Char::isLetter) -> "Password must contain at least one letter"
        !value.any(Char::isDigit) -> "Password must contain at least one digit"
        else -> null
    }

    fun validateCurrency(value: String): String? =
        if (!currencyPattern.matches(value)) "Currency must be an ISO-style three-letter code" else null

    fun validateHeightCm(value: Int): String? =
        if (value !in 50..250) "Height must be between 50 and 250 cm" else null

    fun validateBirthday(value: Birthday): String? {
        if (value.month !in 1..12) return "Birthday month must be between 1 and 12"
        if (value.year != null && value.year !in 1900..2100) return "Birthday year is invalid"

        val maxDay = when (value.month) {
            2 -> if (value.year == null || isLeapYear(value.year)) 29 else 28
            4, 6, 9, 11 -> 30
            else -> 31
        }
        return if (value.day !in 1..maxDay) "Birthday day is invalid for this month" else null
    }

    private fun isLeapYear(year: Int): Boolean = year % 400 == 0 || (year % 4 == 0 && year % 100 != 0)
}
